import { type SemanticQueryResponse, semanticQueryResponseSchema } from '@data-hub/shared';

/**
 * Tiny localStorage persister for semantic-query results, scoped to exactly
 * this query family — a page reload paints charts INSTANTLY from local data
 * while TanStack revalidates per its normal staleTime.
 *
 * Manual on purpose (no @tanstack/react-query-persist-client dep): we only
 * want one query family persisted, with per-entry zod validation on read
 * (CLAUDE.md: validate at every storage edge) and a hard size budget.
 * FAIL-OPEN everywhere: parse/quota/JSON errors degrade to "no cache" —
 * never a throw in a render path.
 */

const LS_KEY = 'metric-hub.explore.query-cache.v1'; // bump suffix to invalidate wholesale
const MAX_ENTRIES = 150;
const MAX_TOTAL_CHARS = 3_000_000; // ~3MB of a 5-10MB origin quota
const MAX_ENTRY_CHARS = 256_000; // one 10k-row result must not evict everything else
const FLUSH_DEBOUNCE_MS = 500; // one write per board load, not one per cell

interface StoredEntry {
  t: number;
  d: unknown;
}

let entries: Map<string, StoredEntry> | null = null;
let flushTimer: number | null = null;

function load(): Map<string, StoredEntry> {
  if (entries) return entries;
  entries = new Map();
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return entries;
    const blob: unknown = JSON.parse(raw);
    if (
      typeof blob === 'object' &&
      blob !== null &&
      'entries' in blob &&
      typeof (blob as { entries: unknown }).entries === 'object'
    ) {
      for (const [k, v] of Object.entries((blob as { entries: Record<string, unknown> }).entries)) {
        if (
          typeof v === 'object' &&
          v !== null &&
          't' in v &&
          typeof (v as StoredEntry).t === 'number' &&
          'd' in v
        ) {
          entries.set(k, v as StoredEntry);
        }
      }
    }
  } catch {
    // Corrupt blob → start empty; the next write replaces it.
  }
  return entries;
}

function scheduleFlush(): void {
  if (flushTimer !== null) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    persist();
  }, FLUSH_DEBOUNCE_MS);
}

function persist(): void {
  const map = load();
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify({ v: 1, entries: Object.fromEntries(map) }));
  } catch {
    // QuotaExceeded (or storage disabled): drop our bucket and retry once.
    try {
      window.localStorage.removeItem(LS_KEY);
      window.localStorage.setItem(
        LS_KEY,
        JSON.stringify({ v: 1, entries: Object.fromEntries(map) }),
      );
    } catch {
      // Give up silently — this is a cache.
    }
  }
}

function evictWhileOver(map: Map<string, StoredEntry>): void {
  const overBudget = () => {
    if (map.size > MAX_ENTRIES) return true;
    let total = 0;
    for (const v of map.values()) total += JSON.stringify(v.d).length;
    return total > MAX_TOTAL_CHARS;
  };
  while (map.size > 0 && overBudget()) {
    // Evict the oldest by timestamp.
    let oldestKey: string | null = null;
    let oldestT = Number.POSITIVE_INFINITY;
    for (const [k, v] of map) {
      if (v.t < oldestT) {
        oldestT = v.t;
        oldestKey = k;
      }
    }
    if (oldestKey === null) break;
    map.delete(oldestKey);
  }
}

export function readQueryResult(
  hash: string,
): { data: SemanticQueryResponse; t: number } | undefined {
  try {
    const entry = load().get(hash);
    if (!entry) return undefined;
    const parsed = semanticQueryResponseSchema.safeParse(entry.d);
    if (!parsed.success) {
      load().delete(hash); // corrupt/foreign entry — drop, never throw
      return undefined;
    }
    return { data: parsed.data, t: entry.t };
  } catch {
    return undefined;
  }
}

export function writeQueryResult(hash: string, data: SemanticQueryResponse): void {
  try {
    if (JSON.stringify(data).length > MAX_ENTRY_CHARS) return; // oversized — skip
    const map = load();
    map.set(hash, { t: Date.now(), d: data });
    evictWhileOver(map);
    scheduleFlush();
  } catch {
    // Never let cache writes break the query path.
  }
}

/** Wipe the bucket (sign-out, or wholesale invalidation). */
export function clearQueryResultCache(): void {
  entries = new Map();
  try {
    window.localStorage.removeItem(LS_KEY);
  } catch {
    // ignore
  }
}
